// Single source of truth for poster widget options:
// - what fonts / sizes / alignment values we allow
// - what the editor inspector exposes
// - what the backend whitelist (TournamentPremiumCanvasLayoutParser) must accept
//
// Keep this in sync with TournamentPremiumCanvasLayoutParser.cs (AllowedWidgetOptions).

export const FONT_FAMILIES = [
  { id: "display", label: "Display (Syne)", css: '"Syne", system-ui, sans-serif' },
  { id: "sans", label: "Sans (DM Sans)", css: '"DM Sans", system-ui, sans-serif' },
  { id: "serif", label: "Serif", css: "Georgia, 'Times New Roman', serif" },
  { id: "georgia", label: "Georgia", css: "Georgia, serif" },
  { id: "arial", label: "Arial", css: "Arial, Helvetica, sans-serif" },
  { id: "verdana", label: "Verdana", css: "Verdana, Geneva, sans-serif" },
  { id: "courier", label: "Courier", css: "'Courier New', Courier, monospace" },
];

export const FONT_FAMILY_IDS = FONT_FAMILIES.map((f) => f.id);

export function fontFamilyCss(id) {
  const hit = FONT_FAMILIES.find((f) => f.id === id);
  return hit ? hit.css : FONT_FAMILIES[1].css;
}

export const FONT_SIZE_MIN = 8;
export const FONT_SIZE_MAX = 22;

// Free-form text blocks allow a wider size range than data widgets (titles).
// Mirror MinTextFontSize / MaxTextFontSize in TournamentPremiumCanvasLayoutParser.cs.
export const TEXT_FONT_SIZE_MIN = 8;
export const TEXT_FONT_SIZE_MAX = 160;
export const TEXT_MAX_LENGTH = 2000;

export const ALIGN_VALUES = ["left", "center", "right"];

/** True for "#rgb" / "#rrggbb" (case-insensitive). */
export function isHexColor(value) {
  return typeof value === "string" && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

/**
 * Default props for a free-form text block. These live at the block level
 * (alongside id/type/x/y/w/h), not inside widgetOptions. Keep the keys in sync
 * with ValidateTextBlock in TournamentPremiumCanvasLayoutParser.cs.
 */
export function defaultTextBlock() {
  return {
    text: "Texto",
    fontFamily: "display",
    fontSize: 28,
    color: "#111827",
    bgColor: "",
    align: "left",
    bold: true,
    italic: false,
  };
}

/** Merge a stored text block over the defaults and bound its values for rendering. */
export function resolveTextBlock(block) {
  const def = defaultTextBlock();
  const raw = block && typeof block === "object" ? block : {};
  const merged = {
    text: typeof raw.text === "string" ? raw.text : def.text,
    fontFamily: FONT_FAMILY_IDS.includes(raw.fontFamily) ? raw.fontFamily : def.fontFamily,
    fontSize: def.fontSize,
    color: isHexColor(raw.color) ? raw.color : def.color,
    bgColor: isHexColor(raw.bgColor) ? raw.bgColor : "",
    align: ALIGN_VALUES.includes(raw.align) ? raw.align : def.align,
    bold: raw.bold !== undefined ? !!raw.bold : def.bold,
    italic: !!raw.italic,
  };
  const n = Number(raw.fontSize);
  if (Number.isFinite(n)) {
    merged.fontSize = Math.max(TEXT_FONT_SIZE_MIN, Math.min(TEXT_FONT_SIZE_MAX, Math.round(n)));
  }
  return merged;
}

export const GROUP_COLUMNS = ["shield", "points", "goalsFor", "goalsAgainst", "played"];

export const BRACKET_LAYOUTS = ["list", "rounds", "fields"];

/**
 * Default options per widget kind. Used when reading a block without options
 * and when normalizing user-edited values.
 */
export function defaultWidgetOptions(kind) {
  switch (kind) {
    case "groups":
      return {
        fontFamily: "sans",
        fontSize: 10,
        showShield: true,
        columns: {
          shield: true,
          points: true,
          goalsFor: true,
          goalsAgainst: true,
          played: true,
        },
        align: {
          team: "left",
          numbers: "center",
        },
        // Optional ordering of group columns (by group id). Empty = natural
        // order from the API (typically alphabetical).
        columnsOrder: [],
      };
    case "matches":
      return {
        fontFamily: "sans",
        fontSize: 10,
        showField: true,
        align: {
          teams: "left",
          numbers: "center",
        },
        // Optional ordering of field columns (by field name).
        columnsOrder: [],
      };
    case "schedule":
      return {
        fontFamily: "sans",
        fontSize: 10,
        showGroupLetter: true,
        // Empty box between teams so mesa/table can write the result by hand.
        showResultBox: false,
        // Alignment of the match (teams) inside each cell.
        align: {
          match: "left",
        },
        // Optional ordering of field columns (by field name).
        columnsOrder: [],
      };
    case "bracket":
      return {
        fontFamily: "sans",
        fontSize: 10,
        bracketLayout: "rounds", // "list" | "rounds"
        showFeederLabels: true,
        showShield: false,
        // Optional ordering of round columns (by depth label: "Final",
        // "Semifinal", "Cuartos", "Octavos", "16avos", "Ronda N"). Empty
        // = deepest stage left → final right.
        columnsOrder: [],
      };
    default:
      return {
        fontFamily: "sans",
        fontSize: 10,
        columnsOrder: [],
      };
  }
}

/**
 * Apply an optional preferred order to a list of items identified by a key.
 * Items present in `order` come first in that order; items not listed keep
 * their natural order at the end. Unknown keys in `order` are ignored.
 *
 *   const items = [{ id: "a" }, { id: "b" }, { id: "c" }];
 *   applyColumnsOrder(items, ["c", "a"], x => x.id)
 *     // → [c, a, b]
 */
export function applyColumnsOrder(items, order, getKey) {
  if (!Array.isArray(items) || items.length === 0) return items || [];
  if (!Array.isArray(order) || order.length === 0) return items.slice();
  const byKey = new Map(items.map((it, i) => [String(getKey(it, i) ?? ""), it]));
  const used = new Set();
  const out = [];
  for (const k of order) {
    const key = String(k ?? "");
    if (!key || used.has(key)) continue;
    if (byKey.has(key)) {
      out.push(byKey.get(key));
      used.add(key);
    }
  }
  for (const it of items) {
    const key = String(getKey(it) ?? "");
    if (!used.has(key)) out.push(it);
  }
  return out;
}

/**
 * Merge user-edited options on top of defaults for a given kind.
 * Used both at read time (widgets) and at write time (inspector edits).
 */
export function resolveWidgetOptions(kind, raw) {
  const def = defaultWidgetOptions(kind);
  if (!raw || typeof raw !== "object") return def;
  const merged = { ...def, ...raw };
  if (kind === "groups") {
    merged.columns = { ...def.columns, ...(raw.columns || {}) };
    merged.align = { ...def.align, ...(raw.align || {}) };
  }
  if (kind === "matches" || kind === "schedule") {
    merged.align = { ...def.align, ...(raw.align || {}) };
  }
  if (!FONT_FAMILY_IDS.includes(merged.fontFamily)) merged.fontFamily = def.fontFamily;
  const n = Number(merged.fontSize);
  merged.fontSize = Number.isFinite(n) ? Math.max(FONT_SIZE_MIN, Math.min(FONT_SIZE_MAX, Math.round(n))) : def.fontSize;
  if (kind === "bracket" && !BRACKET_LAYOUTS.includes(merged.bracketLayout)) {
    merged.bracketLayout = def.bracketLayout;
  }
  return merged;
}
